import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

/**
 * Checkout-wide mirror of the active user journey owned by the userJourney integration
 * (`src/integrations/userJourney/`). The integration context is the source of truth;
 * `UserJourneyStateSync` writes through to this slice synchronously from the journey
 * dispatch path, so non-React code (thunks, error handlers) can read the current journey
 * correlation id even in the same tick as a journey mutation. Deliberately separate from
 * `cashAuth.journeyId`, which is scoped to the Cash auth ceremony.
 *
 * Persistence: blacklisted in store.ts — a rehydrated page load must never resurrect a
 * stale journey as active (a reload starts a new journey; abandonment is derived
 * analytically from started/completed events).
 */
export interface ActiveUserJourney {
  name: string
  journeyId: string
  createdAt: number
  variant?: string
  tags: string[]
  frustrations: string[]
  frictions: string[]
}

export interface UserJourneyState {
  active?: ActiveUserJourney
}

export const userJourneyInitialState: UserJourneyState = {
  active: undefined,
}

const userJourneySlice = createSlice({
  name: 'userJourney',
  initialState: userJourneyInitialState,
  reducers: {
    syncActiveJourney(state, action: PayloadAction<ActiveUserJourney | undefined>) {
      state.active = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => userJourneyInitialState)
  },
})

export const { syncActiveJourney } = userJourneySlice.actions

export default userJourneySlice.reducer
